import { connect } from 'react-redux';
import { get } from 'lodash';
import i18next from 'i18next';
import React, { useState, useEffect } from 'react';

import utilsXLS from '@commons/utils/makeXLS';

import ExportModalContent from '@lib/inpulse/ExportModal';

import { getClientInfo } from '@selectors/client';

import { product as productService } from '@services/product';

const BATCH_PRODUCTS_TO_FETCH_AT_ONCE = 50;

const getColumnsSettings = () => [
  {
    propertyKey: 'id',
    name: i18next.t('GENERAL.ID'),
  },
  {
    propertyKey: 'name',
    name: i18next.t('GENERAL.NAME'),
  },
  {
    propertyKey: 'sku',
    name: i18next.t('GENERAL.SKU'),
  },
  {
    propertyKey: 'category',
    name: i18next.t('GENERAL.CATEGORY'),
  },
  {
    propertyKey: 'subCategory',
    name: i18next.t('GENERAL.SUB_CATEGORY'),
  },
  {
    propertyKey: 'shelfLife',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_SHELF_LIFE'),
  },
  {
    propertyKey: 'portionSizeInGrams',
    name: i18next.t('ADMIN.PRODUCTS.PORTION_SIZE_IN_GRAMS'),
  },
  {
    propertyKey: 'allergens',
    name: i18next.t('GENERAL.ALLERGENS'),
  },
  {
    propertyKey: 'detailedComposition',
    name: i18next.t('ADMIN.PRODUCTS.DETAILED_COMPOSITION'),
  },
  {
    propertyKey: 'nutritionFacts.energy',
    name: i18next.t('GENERAL.PER_100G_ENERGY'),
  },
  {
    propertyKey: 'nutritionFacts.fat',
    name: i18next.t('GENERAL.PER_100G_FAT'),
  },
  {
    propertyKey: 'nutritionFacts.saturatedFat',
    name: i18next.t('GENERAL.PER_100G_SATURATED_FAT'),
  },
  {
    propertyKey: 'nutritionFacts.carbohydrates',
    name: i18next.t('GENERAL.PER_100G_CARBOHYDRATES'),
  },
  {
    propertyKey: 'nutritionFacts.sugar',
    name: i18next.t('GENERAL.PER_100G_SUGAR'),
  },
  {
    propertyKey: 'nutritionFacts.fiber',
    name: i18next.t('GENERAL.PER_100G_FIBER'),
  },
  {
    propertyKey: 'nutritionFacts.protein',
    name: i18next.t('GENERAL.PER_100G_PROTEIN'),
  },
  {
    propertyKey: 'nutritionFacts.salt',
    name: i18next.t('GENERAL.PER_100G_SALT'),
  },
  {
    type: 'currency',
    propertyKey: 'priceWithoutTaxes',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_PRICE_HT'),
  },
  {
    propertyKey: 'vatRate',
    name: i18next.t('GENERAL.VAT_RATE'),
  },
  {
    type: 'currency',
    propertyKey: 'priceWithTaxes',
    name: i18next.t('ADMIN.PRODUCTS.EXPORT_COLUMN_PRICE_INCLUDING_TAXES'),
  },
  {
    propertyKey: 'currency',
    name: i18next.t('GENERAL.CURRENCY'),
  },
];

/**
 * Update the progress value from the elements that are still left to treat
 *
 * @param {String[]} productIds           - The list of product ids that are still to be treated
 * @param {Object[]} processedProducts  - The list of product that has already been treated
 * @param {Function} setProgress          - Method to set the local state of the progress value
 *
 * @return {void}
 */
export function updateProgress(productIds, processedProducts, setProgress) {
  const productLeft = productIds.length;
  const totalProducts = productLeft + processedProducts.length;
  const updatedProgress = 100 - (productLeft / totalProducts) * 100;

  setProgress(updatedProgress);
}

/**
 * Handle the progressive loading of products from the list of productIds that are still to fetch
 *
 * @returns {void}
 */
export async function loadProductsByBatchOfIds(
  productIds,
  processedProducts,
  setLoading,
  setTitle,
  setProgress,
  setProductIds,
  setDetailedProductList,
  currency,
) {
  if (!productIds.length && !processedProducts.length) {
    return;
  }

  const columnsSettings = getColumnsSettings();

  // If no more ids, then all data has been fetched
  if (!productIds.length) {
    setProgress(100);

    const sheet = utilsXLS.generateDefaultSheet(
      i18next.t('ADMIN.PRODUCTS.INCO_DATA_EXPORT_FILENAME'),
      columnsSettings,
      processedProducts,
      currency,
    );

    utilsXLS.makeXLS(i18next.t('ADMIN.PRODUCTS.INCO_DATA_EXPORT_FILENAME'), [sheet]);

    return exportReady(setLoading, setTitle);
  }

  const selectedProductIds = productIds.splice(0, BATCH_PRODUCTS_TO_FETCH_AT_ONCE);

  // Perform request
  try {
    const productsExportData = await productService.exportIncoData(selectedProductIds);

    let productItemsList = [...processedProducts];

    for (const data of productsExportData) {
      productItemsList = productItemsList.concat(data);
    }

    setDetailedProductList(productItemsList);

    updateProgress(productIds, productItemsList, setProgress);

    setProductIds(productIds);
  } catch (err) {
    exportFailure(setLoading, setTitle);
  }
}

/**
 * Close the modal
 *
 * @returns {void}
 */
export function exitModal(setLoading, closeModal) {
  setLoading(false);

  closeModal();
}

/**
 * Set component with export success state
 *
 * @returns {void}
 */
export function exportReady(setLoading, setTitle) {
  setTitle(i18next.t('GENERAL.EXPORT_SUCCESS'));
  setLoading(false);
}

/**
 * Set component with export failure state
 *
 * @returns {void}
 */
export function exportFailure(setLoading, setTitle) {
  setTitle(i18next.t('GENERAL.EXPORT_FAILURE'));
  setLoading(false);
}

export const IncoExportModal = (props) => {
  const {
    params: { title, products, customParams, selectedStatus },
    currency,
    closeModal,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [titleModal, setTitle] = useState(title || '');
  const [productIdsToProcess, setProductIds] = useState([]);
  const [processedProducts, setProcessedProducts] = useState([]);

  // States for fetching all products by filters
  const [currentPage, setCurrentPage] = useState(1);
  const [allProductIds, setAllProductIds] = useState([]);
  const [fetchingPhase, setFetchingPhase] = useState('initial'); // 'initial', 'fetching_ids', 'processing_details', 'completed'

  const fetchNextBatch = async () => {
    try {
      const { data, count } = await productService.getPaginatedProductsOfClient(
        {
          orderBy: 'name',
          orderType: 'asc',
          page: currentPage,
          limit: 1000,
          search: '', // Don't apply search for export
        },
        {
          brandId: customParams.brandId,
          active: { eq: get(selectedStatus, 'filterValue', false) },
          categoryId: customParams.categoryId,
          isIngredient: customParams.isIngredient,
          subCategoryId: customParams.subCategoryId,
          priceWithTaxes: customParams.priceWithTaxes,
          nbCashierProductMappingCount: customParams.nbCashierProductMappingCount,
        },
      );

      const productIds = (data || []).map(({ id }) => id);

      // Update allProductIds with new batch
      const updatedAllProductIds = [...allProductIds, ...productIds];
      setAllProductIds(updatedAllProductIds);

      // Check if we have more data to fetch
      if (
        data &&
        data.length === BATCH_PRODUCTS_TO_FETCH_AT_ONCE &&
        updatedAllProductIds.length < count
      ) {
        // Continue with next batch
        setCurrentPage(currentPage + 1);
      } else {
        // All product IDs fetched, now start the detailed export process
        setFetchingPhase('processing_details');
        setProductIds([...updatedAllProductIds]);
      }
    } catch {
      exportFailure(setLoading, setTitle);
    }
  };

  // Initialize: either use provided products or fetch all by filters
  useEffect(() => {
    if (products && products.length) {
      // Export selected products directly
      const productIds = products.map(({ id }) => id);
      setProductIds(productIds);
      setFetchingPhase('processing_details');
    } else if (customParams && selectedStatus) {
      // Start fetching all products by filters
      setFetchingPhase('fetching_ids');
    }
  }, []);

  // Fetch products batch by batch when in fetching_ids phase
  useEffect(() => {
    if (fetchingPhase !== 'fetching_ids') {
      return;
    }

    fetchNextBatch();
  }, [currentPage, fetchingPhase]);

  // Handle the detailed export process
  useEffect(() => {
    if (fetchingPhase === 'processing_details') {
      loadProductsByBatchOfIds(
        [...productIdsToProcess],
        processedProducts,
        setLoading,
        setTitle,
        setProgress,
        setProductIds,
        setProcessedProducts,
        currency,
      );
    }
  }, [productIdsToProcess, fetchingPhase]);

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps)(IncoExportModal);
